/* Warteschlangen-Ansicht: aktive Jobs (Benutzer: die eigenen mit Platz, Admin: alle mit Besitzer) und der Verlauf.
 * Fragt im Takt ab, solange jemand zuschaut (start/stop). Ohne DOM, Zeitgeber werden injiziert. */
import type { BackendClient, CompileJob, QueueInfo } from './backend.ts';
import { isActive } from './compile.ts';

export interface QueueSnapshot {
  version: number;
  info: QueueInfo | null;
  /** zuletzt beendete Jobs, neueste zuerst */
  history: CompileJob[];
  error: string | null;
  /** Job, dessen Abbruch läuft */
  busy: string | null;
}

export interface QueueDeps {
  client: () => BackendClient | null;
  t: (key: string, params?: Record<string, unknown>) => string;
  /** Admin: Verlauf und Liste umfassen alle Benutzer */
  isAdmin: () => boolean;
  interval?: number;
  historySize?: number;
  setTimer?: (f: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
}

export class QueueLink {
  private deps: QueueDeps;
  private info: QueueInfo | null = null;
  private history: CompileJob[] = [];
  private error: string | null = null;
  private busy: string | null = null;
  private version = 0;
  private snap: QueueSnapshot;
  private listeners = new Set<() => void>();
  private timer: unknown = null;
  private running = false;
  private gen = 0;
  private loading = false;

  constructor(deps: QueueDeps) { this.deps = deps; this.snap = this.build(); }
  private build(): QueueSnapshot { return { version: this.version, info: this.info, history: this.history, error: this.error, busy: this.busy }; }
  private emit(): void { this.version++; this.snap = this.build(); this.listeners.forEach((f) => f()); }
  subscribe = (fn: () => void): (() => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  getSnapshot = (): QueueSnapshot => this.snap;

  private setT = (f: () => void, ms: number): unknown => (this.deps.setTimer ? this.deps.setTimer(f, ms) : setTimeout(f, ms));
  private clearT = (t: unknown): void => { if (this.deps.clearTimer) this.deps.clearTimer(t); else clearTimeout(t as ReturnType<typeof setTimeout>); };
  private client(): BackendClient { const c = this.deps.client(); if (!c) throw new Error('kein Backend'); return c; }

  /** Abfrage beginnen (sofort, dann im Takt) */
  start(): void {
    if (this.running) return;
    this.running = true; this.gen++;
    void this.load(this.gen);
  }
  stop(): void {
    this.running = false; this.gen++;
    if (this.timer) { this.clearT(this.timer); this.timer = null; }
  }

  /** Einmal neu laden (z. B. nach einem Abbruch) */
  refresh(): Promise<void> { return this.load(this.gen); }

  private async load(gen: number): Promise<void> {
    if (gen !== this.gen || this.loading) return;
    this.loading = true;
    try {
      const c = this.client();
      const [info, list] = await Promise.all([c.compileQueue(), c.compileList(this.deps.isAdmin())]);
      if (gen !== this.gen) return;
      this.info = info; this.history = list.filter((j) => !isActive(j)).slice(0, this.deps.historySize ?? 10); this.error = null;
    } catch (e) {
      if (gen !== this.gen) return;
      this.error = this.deps.t('queue.load_failed', { msg: (e as Error).message });
    } finally { this.loading = false; }
    if (gen !== this.gen) return;
    this.emit();
    if (this.running) this.timer = this.setT(() => { this.timer = null; void this.load(gen); }, this.deps.interval ?? 3000);
  }

  /** Einen Job abbrechen (eigene; Admins auch fremde) */
  async cancel(id: string): Promise<void> {
    this.busy = id; this.emit();
    let failed: string | null = null;
    try { await this.client().compileCancel(id); }
    catch (e) { failed = this.deps.t('compile.cancel_failed', { msg: (e as Error).message }); }
    this.busy = null;
    await this.refresh();
    if (failed) this.error = failed;   // nach dem Neuladen, das einen alten Fehler sonst löschen würde
    this.emit();
  }
}
